import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Plus,
  RefreshCw,
  FileBox,
  Trash2,
  Info,
  Search,
  MapPin,
  MapPinOff,
  ExternalLink,
} from "lucide-react";
import { Button } from "../components/ui/Button";
import { Badge } from "../components/ui/Badge";
import { Input } from "../components/ui/Input";
import { LoadingSpinner } from "../components/ui/LoadingSpinner";
import { cn } from "../lib/utils";
import { useAppStore } from "../stores/appStore";
import { useAuthStore } from "../stores/authStore";
import { canAdminOrg } from "../lib/permissions";
import {
  type DigitalTwinListItem,
  deleteDigitalTwin,
  listDigitalTwins,
  DIGITAL_TWIN_MAP_PALETTE,
  digitalTwinShortLabel,
  contrastText,
} from "../lib/digitalTwinsApi";
import { UploadDigitalTwinModal } from "../components/digital-twins/UploadDigitalTwinModal";
import { DigitalTwinDetailsModal } from "../components/digital-twins/DigitalTwinDetailsModal";
import { DigitalTwinsMap } from "../components/digital-twins/DigitalTwinsMap";

function statusVariant(s: string): "success" | "danger" | "warning" {
  return s === "ready" ? "success" : s === "error" ? "danger" : "warning";
}

export default function DigitalTwins() {
  const navigate = useNavigate();
  const { selectedOrgId, selectedSiteId } = useAppStore();
  const { user, organizations } = useAuthStore();
  const isAdmin = canAdminOrg(user, organizations, selectedOrgId);

  const [twins, setTwins] = useState<DigitalTwinListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [detailsId, setDetailsId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  // Shared hover key so the list and the map cross-highlight each other.
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  // Tracks which row the user clicked Open on so the button shows a spinner
  // while React unmounts this page and the viewer's API calls + splat
  // download begin. requestAnimationFrame gives the browser a chance to
  // paint the spinner before navigate() blows the page away.
  const [openingId, setOpeningId] = useState<string | null>(null);

  function openTwin(id: string) {
    if (openingId) return;
    setOpeningId(id);
    requestAnimationFrame(() => navigate(`/digital-twins/${id}`));
  }

  const fetchTwins = useCallback(async () => {
    if (!selectedSiteId) {
      setTwins([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      // Fetch every twin for the site (not just page 1) so the map plots them
      // all. page_size is capped at 100 server-side, so loop if there's more.
      const pageSize = 100;
      const first = await listDigitalTwins({
        virtual_site_id: selectedSiteId,
        page: 1,
        page_size: pageSize,
      });
      let all = first.items || [];
      const totalPages = first.total_pages || 1;
      for (let p = 2; p <= totalPages; p++) {
        const res = await listDigitalTwins({
          virtual_site_id: selectedSiteId,
          page: p,
          page_size: pageSize,
        });
        all = all.concat(res.items || []);
      }
      setTwins(all);
    } catch {
      setTwins([]);
    } finally {
      setIsLoading(false);
    }
  }, [selectedSiteId]);

  useEffect(() => {
    fetchTwins();
  }, [fetchTwins]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return twins;
    return twins.filter(
      (t) =>
        t.name.toLowerCase().includes(q) ||
        (t.description || "").toLowerCase().includes(q),
    );
  }, [twins, search]);

  const geoCount = useMemo(
    () => filtered.filter((t) => t.latitude != null && t.longitude != null).length,
    [filtered],
  );

  // One colour per twin, assigned by list position. Shared by the map tags and
  // the list chips so a twin's colour is the same in both — the list is the
  // legend for the map's colour-coded tags.
  const colorById = useMemo(() => {
    const m = new Map<string, string>();
    filtered.forEach((t, i) =>
      m.set(t.id, DIGITAL_TWIN_MAP_PALETTE[i % DIGITAL_TWIN_MAP_PALETTE.length]),
    );
    return m;
  }, [filtered]);

  async function handleDelete(id: string, name: string) {
    if (!confirm(`Delete digital twin "${name}"? This removes the splat from S3.`)) return;
    try {
      await deleteDigitalTwin(id);
      fetchTwins();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } } };
      alert(e.response?.data?.detail || "Delete failed");
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-surface-100">Digital Twins</h1>
          <p className="text-sm text-surface-400 mt-1">
            3D Gaussian Splatting models on a satellite map, viewed with Spark JS
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-64">
            <Input
              placeholder="Search digital twins..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              icon={<Search className="h-4 w-4" />}
            />
          </div>
          <Button variant="secondary" size="icon" onClick={fetchTwins}>
            <RefreshCw className="h-4 w-4" />
          </Button>
          {isAdmin && (
            <Button
              onClick={() => setShowUpload(true)}
              disabled={!selectedSiteId}
              title={!selectedSiteId ? "Select a site first" : undefined}
            >
              <Plus className="h-4 w-4" />
              Upload Twin
            </Button>
          )}
        </div>
      </div>

      {!selectedSiteId ? (
        <div className="rounded-xl border border-surface-700/50 bg-surface-900/40 px-6 py-12 text-center">
          <FileBox className="mx-auto h-10 w-10 text-surface-600" />
          <p className="mt-3 text-sm text-surface-300">
            Select a virtual site from the top bar to list its digital twins.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-4 lg:flex-row">
          {/* Map */}
          <div className="h-[70vh] lg:w-3/5">
            <DigitalTwinsMap
              twins={filtered}
              colorById={colorById}
              hoveredId={hoveredId}
              onHover={setHoveredId}
              onOpen={openTwin}
            />
          </div>

          {/* List */}
          <div className="flex h-[70vh] flex-col overflow-hidden rounded-xl border border-surface-700/50 bg-surface-900/40 lg:w-2/5">
            <div className="flex items-center justify-between border-b border-surface-700/50 px-4 py-2.5">
              <span className="text-sm font-medium text-surface-200">
                {filtered.length} model{filtered.length === 1 ? "" : "s"}
              </span>
              <span className="text-xs text-surface-500">
                {geoCount} on map
              </span>
            </div>

            <div className="flex-1 overflow-y-auto">
              {isLoading ? (
                <div className="flex items-center justify-center py-16">
                  <LoadingSpinner size="lg" label="Loading digital twins..." />
                </div>
              ) : filtered.length === 0 ? (
                <div className="px-6 py-16 text-center text-sm text-surface-400">
                  {isAdmin
                    ? "No digital twins yet. Click 'Upload Twin' to add the first one."
                    : "No digital twins for this site yet."}
                </div>
              ) : (
                filtered.map((t) => {
                  const geo = t.latitude != null && t.longitude != null;
                  const opening = openingId === t.id;
                  const color = colorById.get(t.id) ?? "#6366f1";
                  return (
                    <div
                      key={t.id}
                      onMouseEnter={() => setHoveredId(t.id)}
                      onMouseLeave={() => setHoveredId(null)}
                      onClick={() => openTwin(t.id)}
                      className={cn(
                        "group flex cursor-pointer items-center gap-3 border-b border-surface-800/60 px-3 py-2.5 transition-colors",
                        hoveredId === t.id ? "bg-brand-500/10" : "hover:bg-surface-800/40",
                      )}
                    >
                      <div
                        className={cn(
                          "flex h-8 min-w-[2rem] shrink-0 items-center justify-center rounded-lg px-1.5 text-[10px] font-bold tracking-wide transition-transform",
                          hoveredId === t.id && "scale-110",
                        )}
                        style={{ background: color, color: contrastText(color) }}
                        title={t.name}
                      >
                        {digitalTwinShortLabel(t.name)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-medium text-surface-100">{t.name}</p>
                        <div className="mt-0.5 flex items-center gap-2">
                          <Badge variant={statusVariant(t.status)}>{t.status}</Badge>
                          {geo ? (
                            <span className="flex items-center gap-1 text-xs text-surface-500">
                              <MapPin className="h-3 w-3" />
                              {t.latitude!.toFixed(4)}, {t.longitude!.toFixed(4)}
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-xs text-amber-500/80">
                              <MapPinOff className="h-3 w-3" />
                              No location
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-0.5">
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDetailsId(t.id);
                          }}
                          title="View full details"
                        >
                          <Info className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          isLoading={opening}
                          disabled={!!openingId && !opening}
                          onClick={(e) => {
                            e.stopPropagation();
                            openTwin(t.id);
                          }}
                          title="Open in viewer"
                        >
                          {!opening && <ExternalLink className="h-4 w-4" />}
                        </Button>
                        {isAdmin && (
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(t.id, t.name);
                            }}
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4 text-red-400" />
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      {selectedSiteId && (
        <UploadDigitalTwinModal
          isOpen={showUpload}
          onClose={() => setShowUpload(false)}
          virtualSiteId={selectedSiteId}
          onCreated={fetchTwins}
        />
      )}

      <DigitalTwinDetailsModal
        isOpen={detailsId !== null}
        onClose={() => setDetailsId(null)}
        digitalTwinId={detailsId}
        canEdit={isAdmin}
        onUpdated={fetchTwins}
      />
    </motion.div>
  );
}
